import { ArrowUpRightIcon } from "@phosphor-icons/react"
import { useRef, type MouseEvent } from "react"
import type { IRowNode } from "ag-grid-community"

import { Button, IconButton } from "~/components/reusable/Button"
import { ButtonGroup } from "~/components/reusable/ButtonGroup"
import { ShortcutTooltip } from "~/components/reusable/ShortcutTooltip"
import { HOTKEYS } from "~/constants/hotkeys"
import InspectorToggleIcon from "~/components/design-system/pages/internal/InspectorToggleIcon"
import { useInspectorContext } from "~/components/design-system/pages/internal/InspectorContext"
import {
  useIsHighlightedRow,
  type HighlightSubscription,
} from "~/components/design-system/pages/internal/highlightSubscription"
import {
  usePublishHoveredRow,
  useWasRowHovered,
} from "~/components/design-system/pages/internal/hoveredRow"

type InspectorPeekCellButtonProps = {
  /** The grid row this button sits in, as ag-grid ids it. */
  rowId: string
  /** The ids standing for this row in the highlight (a group's leaves). Defaults to `rowId`. */
  highlightIds?: readonly string[]
  /** The table's published highlight. */
  subscription: HighlightSubscription
  /** Highlight this row. The button reveals the inspector itself. */
  onOpen: (rowId: string) => void
  /** Drop the highlight. The button collapses the inspector itself. */
  onClose: () => void
  /** `newTab` is ⌘/Ctrl on click; always false from `Shift+O`. */
  onOpenElsewhere?: (newTab: boolean) => void
  openElsewhereLabel?: string
  /** The grid row (the cell's `params.node`): drives the lazy mount and the row's shortcuts. */
  rowNode: IRowNode
}

/**
 * In-cell peek control: opens the Module inspector on this row, or closes it when the row is the
 * one on show. Same glyph and crossfade as the toolbar toggle (one control at two scales),
 * labelled here.
 *
 * Subscribes to the highlight rather than taking a prop, so column definitions stay stable and
 * only rows entering or leaving it re-render. On a group row it passes its leaves'
 * `highlightIds` and is on show when all are highlighted (the inspector's multi state).
 *
 * Renders nothing when the Module has no registered inspector. Use only where clicking a row
 * drives the inspector.
 *
 * On the row under the pointer, `I` (`HOTKEYS.inspectHoveredRow`) presses the toggle and `Shift+O`
 * (`HOTKEYS.openHoveredRow`) the ↗, so every table with this button gets both shortcuts.
 *
 * The button mounts on the row's first hover (or once the row is highlighted) instead of with the
 * row, so scrolling doesn't render one per row while they are all hidden.
 */
const InspectorPeekCellButton = (props: InspectorPeekCellButtonProps) => {
  const { inspector } = useInspectorContext()
  const isHighlighted = useIsHighlightedRow(props.subscription, props.highlightIds ?? props.rowId)
  const wasHovered = useWasRowHovered(props.rowNode)
  if (!inspector || (!wasHovered && !isHighlighted)) return null
  return <PeekControls {...props} isHighlighted={isHighlighted} />
}

const PeekControls = ({
  rowId,
  isHighlighted,
  onOpen,
  onClose,
  onOpenElsewhere,
  openElsewhereLabel = "Open variant",
  rowNode,
}: InspectorPeekCellButtonProps & { isHighlighted: boolean }) => {
  const { isOpen, toggle, open } = useInspectorContext()
  // What the button meant at press time: the highlight moves on mousedown (cell focus), so by
  // click the live value would read "Close" on a row the planner meant to open.
  const wasShowingOnPress = useRef(false)

  // A collapsed inspector leaves the row highlighted; pressing then reopens rather than clears.
  const showingThisRow = isHighlighted && isOpen

  const toggleRow = (wasShowing: boolean) => {
    if (wasShowing) {
      onClose()
      toggle()
      return
    }
    onOpen(rowId)
    open()
  }

  // No mousedown races a key press, so the key reads the live state.
  usePublishHoveredRow(rowNode, {
    toggle: () => toggleRow(showingThisRow),
    openElsewhere: onOpenElsewhere && (() => onOpenElsewhere(false)),
  })

  const handleMouseDown = () => {
    wasShowingOnPress.current = showingThisRow
  }

  const handleClick = (e: MouseEvent) => {
    // The cell click would otherwise re-highlight the row we just cleared.
    e.stopPropagation()
    toggleRow(wasShowingOnPress.current)
  }

  const toggleButton = (
    <ShortcutTooltip
      label={showingThisRow ? "Close inspector" : "Inspect"}
      shortcut={HOTKEYS.inspectHoveredRow.hotkey}
      side="bottom"
    >
      <Button
        variant="neutral"
        size="xs"
        className="filled-surface-button gap-1.5"
        // Read by the cell's hover-reveal CSS: the row on show keeps its button visible.
        data-peek-active={showingThisRow}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      >
        <InspectorToggleIcon isOpen={showingThisRow} />
        {showingThisRow ? "Close" : "Inspect"}
      </Button>
    </ShortcutTooltip>
  )

  return (
    <div className="inspector-peek-button flex items-center">
      {onOpenElsewhere ? (
        <ButtonGroup aria-label="Row actions">
          {toggleButton}
          <ShortcutTooltip
            label={openElsewhereLabel}
            shortcut={HOTKEYS.openHoveredRow.hotkey}
            side="bottom"
          >
            <IconButton
              variant="neutral"
              size="xs"
              className="filled-surface-button"
              aria-label={openElsewhereLabel}
              onClick={(e) => {
                e.stopPropagation()
                onOpenElsewhere(e.metaKey || e.ctrlKey)
              }}
            >
              <ArrowUpRightIcon />
            </IconButton>
          </ShortcutTooltip>
        </ButtonGroup>
      ) : (
        toggleButton
      )}
    </div>
  )
}

export default InspectorPeekCellButton
